/* eslint-disable complexity */
/* eslint-disable no-constant-condition */
import React, { useEffect, useMemo } from 'react'
import { I18nextProvider } from 'react-i18next'
import { ThemeProvider } from 'styled-components'
import './workaround'

import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import config from '@common/configs'
import i18n from '@common/locales'
import { defaultTheme } from '@common/styles'
import { GlobalStyles } from '@common/styles/global'
import Header from '@components/Header'
import { MainContainer } from '@components/MainContainer/styles'
import PaymentConfirm from '@components/PaymentConfirm'
import SalesBehaviourCarousel from '@components/SalesBehaviourCarousel'
import { StyledLink } from '@components/StyledLink/styles'
import useMain from '@hooks/useMain'
import HomePage from '@pages/HomePage'
import { InventoryPage } from '@pages/InvetoryPage'
import { LoadingPage } from '@pages/LoadingPage'
import PaymentsPage from '@pages/PaymentsPage'
import SuccessPage from '@pages/SuccessPage'
import WalletLoginPage from '@pages/WalletLoginPage'
import useStoreMain from '@store/mainStore'
import { IProviderInit, TTheme } from '@types'

import { Server } from 'src/services/api'

Bugsnag.start({
    apiKey: config.general.bugsnag,
    plugins: [new BugsnagPluginReact()],
})

export interface ITicketFlowProperties {
    apiKey: string
    collectionId: string
    initJson?: IProviderInit
    theme?: TTheme
    internationalization?: typeof i18n
}

export const TicketFlow: React.FC<ITicketFlowProperties> = properties => {
    const { isLoading, setMainInit, setIsLoading, setErrorMessage: setErrorCode, mainInit } = useStoreMain()

    const isMainInitStarted = useMemo((): boolean => {
        return Object.keys(mainInit).length > 0
    }, [mainInit])

    useEffect(() => {
        if (isMainInitStarted) setIsLoading(false)
    }, [mainInit])

    const handleGetInit = React.useCallback(async () => {
        try {
            setIsLoading(true)
            const response = await Server.get(`/account/collection/${properties.collectionId}`)

            if (response.success) {
                const data = response.data.collection as IProviderInit
                setMainInit({
                    ...data,
                    salesBehavior: data.salesBehavior.filter(item => item.visible),
                })
            } else {
                if (properties.initJson) {
                    setMainInit({
                        ...properties.initJson,
                        salesBehavior: properties.initJson.salesBehavior.filter(item => item.visible),
                    })
                } else {
                    setErrorCode('Cannot load the collection')
                }
            }
        } catch (error) {
            console.log('[TicketFlowInit-ERROR]', error)
            if (properties.initJson) {
                setMainInit({
                    ...properties.initJson,
                    salesBehavior: properties.initJson.salesBehavior.filter(item => item.visible),
                })
            } else {
                setErrorCode('Cannot load the collection')
            }
        }
    }, [properties.apiKey, properties.collectionId, properties.initJson, setIsLoading, setMainInit])

    useEffect(() => {
        if (isLoading && !isMainInitStarted) handleGetInit()
    }, [isLoading, handleGetInit])

    return (
        <I18nextProvider i18n={properties.internationalization || i18n}>
            <ThemeProvider theme={properties.theme || defaultTheme}>
                <GlobalStyles />
                <MainContainer>
                    <Header />
                    <TicketFlowStateRoutes />
                    <StyledLink href="https://faktura.art/" target="_blank" rel="noopener noreferrer">
                        Powered by Faktura
                    </StyledLink>
                </MainContainer>
            </ThemeProvider>
        </I18nextProvider>
    )
}

const TicketFlowStateRoutes = () => {
    const {
        state: {
            warningMessage,
            currentWallet,
            transactionHash,
            hasMoreThanOneEndpoint,
            selectedSalesBehaviour,
            currentStep,
            mainInit: { hasDeployed },
        },
    } = useMain()

    const { isLoading } = useStoreMain()

    if (!hasDeployed || isLoading) return <LoadingPage hasDeployed={hasDeployed} isLoading={isLoading} />

    if (currentStep === 'payments-wallet') return <PaymentsPage />

    if (currentStep === 'payments') return <WalletLoginPage title="Payment method" />

    if (currentStep === 'login') return <WalletLoginPage title="View your inventory" />

    if (currentStep === 'confirming')
        return (
            <PaymentConfirm
                isLoading
                message={warningMessage}
                currentWallet={currentWallet}
                transactionHash={transactionHash}
            />
        )

    if (currentStep === 'success') return <SuccessPage />

    if (['inventory', 'inventory-useWinter'].includes(currentStep)) return <InventoryPage />

    if (Object.keys(selectedSalesBehaviour).length === 0 && hasMoreThanOneEndpoint) return <SalesBehaviourCarousel />

    return <HomePage />
}

export interface IWrapperElement {
    internationalization: typeof i18n
    children: React.ReactNode
}
